import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import MuiAutocomplete from '@mui/material/Autocomplete'
import type {
  AutocompleteRenderOptionState,
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import {
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useCallback,
} from 'react'
import { Checkbox } from '../checkbox/Checkbox'
import { sanitizeStringForHtmlId } from '../../utils/sanitizeStringForHtmlId'
import { StyledFormTextFieldWithEndIcon } from '../form-wrappers/FormWrappers.styled'
import { FormAutocompleteBadge } from '../form-wrappers/form-autocomplete/FormAutocompleteBadge'

interface AutocompleteOption<Value, ExtendedValueProps> {
  value: Value
  label: string
  extendedValueProps?: ExtendedValueProps
}

interface AutocompleteComponentProps<
  Value extends number | string,
  ExtendedValueProps,
  Multiple extends boolean,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
> {
  label?: string | ReactNode
  name?: string
  ref?: RefObject<HTMLInputElement | null>
  size?: 'medium' | 'small'
  multiple?: Multiple
  disableCloseOnSelect?: boolean
  error?: boolean
  helperText?: string
  value?: Multiple extends true
    ? AutocompleteOption<Value, ExtendedValueProps>[]
    : AutocompleteOption<Value, ExtendedValueProps> | null
  options: AutocompleteOption<Value, ExtendedValueProps>[]
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement> & { key: unknown },
    option: AutocompleteOption<Value, ExtendedValueProps>,
    state: AutocompleteRenderOptionState,
  ) => ReactNode
  loading?: boolean
  onInputChange?: AutocompleteProps<
    AutocompleteOption<Value, ExtendedValueProps>,
    Multiple,
    DisableClearable,
    FreeSolo
  >['onInputChange']
  filterOptions?: AutocompleteProps<
    AutocompleteOption<Value, ExtendedValueProps>,
    Multiple,
    DisableClearable,
    FreeSolo
  >['filterOptions']
  disabled?: boolean
  onChange?: (
    event: SyntheticEvent,
    value: Multiple extends true
      ? AutocompleteOption<Value, ExtendedValueProps>[]
      : AutocompleteOption<Value, ExtendedValueProps> | null,
  ) => void
  slotProps?: AutocompleteProps<
    AutocompleteOption<Value, ExtendedValueProps>,
    Multiple,
    DisableClearable,
    FreeSolo
  >['slotProps']
}

/* TODO WIP from @adil-hublo; trying to build a hublo-opinionated autocomplete compatible with RHF
 *   but also usable with any other state management
 *  */
export const Autocomplete = <
  Value extends string | number,
  ExtendedValueProps,
  Multiple extends boolean,
>({
  label,
  name,
  ref,
  size = 'medium',
  multiple,
  helperText,
  error,
  value,
  renderOption,
  ...autocompleteProps
}: AutocompleteComponentProps<Value, ExtendedValueProps, Multiple>) => {
  const isAutocompleteFocused = document.activeElement === ref?.current

  const defaultRenderOption: (
    props: HTMLAttributes<HTMLLIElement> & { key: unknown },
    option: AutocompleteOption<Value, ExtendedValueProps>,
    state: AutocompleteRenderOptionState,
  ) => ReactNode = useCallback(
    (props, option, { selected }) => {
      const key = option.value
      const label = option.label

      return (
        <li {...props} key={key}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {multiple && (
              <Checkbox
                id={`${name}_option_checkbox_${key}`}
                checked={selected}
              />
            )}
            <Box
              component="span"
              sx={{
                minWidth: 0,
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {label}
            </Box>
          </Box>
        </li>
      )
    },
    [multiple, name],
  )

  return (
    <MuiAutocomplete
      size={size}
      multiple={multiple}
      popupIcon={<KeyboardArrowDown />}
      renderInput={(params) => {
        return (
          <StyledFormTextFieldWithEndIcon
            label={label}
            {...params}
            slotProps={{
              ...params.slotProps,
              htmlInput: {
                size,
                ...params.slotProps.htmlInput,
              },
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {!isAutocompleteFocused && (
                      <FormAutocompleteBadge
                        multiple={multiple}
                        count={Array.isArray(value) ? value.length : undefined}
                      />
                    )}
                    {params.slotProps.input?.endAdornment}
                  </>
                ),
              },
            }}
            inputRef={ref}
            helperText={helperText}
            error={error}
          />
        )
      }}
      renderOption={renderOption || defaultRenderOption}
      // v9's `renderValue` (was `renderTags`) also fires for single-select and
      // would blank the input; only suppress the default value render for the
      // multiple case, where selected values are shown elsewhere.
      renderValue={multiple ? () => null : undefined}
      id={`autocomplete-${sanitizeStringForHtmlId(name || '')}`}
      getOptionKey={(option) =>
        typeof option === 'string' ? option : option.value
      }
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.label
      }
      value={value}
      isOptionEqualToValue={(option, value) => {
        return option.value === value.value
      }}
      {...autocompleteProps}
    />
  )
}
